import { useMutation } from "@apollo/client";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import {
  ErrorCode,
  ExecuteLlmQuickWinResult,
} from "@towersystems/roam-common/lib/generated-types";
import { PricingSuggestion, ScreenContext } from "./common";
import React from "react";
import {
  Button,
  FlatActionList,
  FlatActionListItem,
  Page,
  useFlashMessages,
  Text,
} from "@towersystems/roam-common-native";
import { StackScreenProps } from "@react-navigation/stack";
import { ProductPricingLlmQuickWinRootParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Colors, View } from "react-native-ui-lib";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useTranslation } from "../../../../utilities/i18n";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";

export interface ProductPricingReviewScreenProps
  extends StackScreenProps<
    ProductPricingLlmQuickWinRootParamList,
    "ProductPricingReviewScreen"
  > {}

export const ProductPricingReviewScreen = ({
  navigation,
}: ProductPricingReviewScreenProps) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  const screenContext = React.useContext(ScreenContext);
  const { onSubmit, originalFormValues } = screenContext;

  const [generatedPricingSuggestions, setGeneratedPricingSuggestions] =
    React.useState<PricingSuggestion[] | undefined>(undefined);

  const flashMessagesContext = useFlashMessages();
  const transitionEnded = useHasTransitionEnded(navigation);
  const { t } = useTranslation("product");

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);

  React.useEffect(() => {
    (async () => {
      return handleOnGenerate();
    })();
  }, []);

  const handleOnGenerate = async () => {
    setGeneratedPricingSuggestions(undefined);
    setSelectedIndex(null);
    return m({
      variables: {
        input: {
          product_price_sugestions_quick_win: {
            productId: originalFormValues.id,
            productBarcode: originalFormValues.stockNumber || "",
            productName: originalFormValues.name || "",
            supplierStockCode: originalFormValues.supplierStockCode || "",
            cost: Number(originalFormValues.costPrice) || 0,
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          console.log("Pricing suggestions result:", result.result);
          // const parsed = JSON.parse(result.result);
          const parsed = JSON.parse(result.result.replace(/\n/g, ""));
          setGeneratedPricingSuggestions(parsed.pricingSuggestions);
        } else {
          if (result && "message" in result) {
            throw new Error(result.message);
          }
          flashMessagesContext.showMessage({
            message:
              "Failed to generate pricing suggestions. Check your credentials.",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        const msg = error?.message ?? "Failed to generate pricing suggestions";

        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });

        if (
          [
            ErrorCode.INVALID_LLM_CREDENTIALS_ERROR,
            ErrorCode.MISSING_LLM_STRATEGY_ERROR,
          ].includes(msg)
        ) {
          navigation.goBack();
        }
      });
  };

  if (generatedPricingSuggestions === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const items: FlatActionListItem[] = generatedPricingSuggestions.map(
    (suggestion, index) => ({
      key: `generated-tag-${index}`,
      title: suggestion.strategy,
      onPress: () => setSelectedIndex(index),
      selected: selectedIndex === index,
      subTitle: `GP: $${suggestion.gpDollarValue.toFixed(
        2
      )} | Markup: ${suggestion.markup.toFixed(2)}%`,
      contentStyles: {
        backgroundColor: selectedIndex === index ? "#ebebeb" : undefined,
        padding: 12,
        borderRadius: 8,
      },
      componentRight: (icon) => {
        return (
          <View row centerV style={{ marginLeft: 20 }}>
            <View>
              <Text
                variant="heading1"
                color={selectedIndex ? Colors.action : undefined}
              >
                {moneyToFormattedString(suggestion.price)}
              </Text>
            </View>
            <View style={{ marginLeft: 16 }}>
              {selectedIndex === index ? (
                <Feather name="check" size={24} color={Colors.action} />
              ) : (
                <Feather
                  name="check"
                  size={24}
                  color={Colors.action}
                  style={{
                    opacity: 0,
                  }}
                />
              )}
            </View>
          </View>
        );
      },
      titleContent({ title, subTitle }) {
        return (
          <View>
            {title}
            {subTitle}
            <View style={{ marginTop: 8 }}>
              <Text variant="body" color={Colors.subdued}>
                {suggestion.reasoning}
              </Text>
            </View>
          </View>
        );
      },
    })
  );

  const disabled =
    generatedPricingSuggestions === undefined ||
    generatedPricingSuggestions.length === 0;

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={disabled}
          label={t("button.generate_product_pricing")}
          isAction
          onPress={() => handleOnGenerate()}
          icon={<Ionicons name="sparkles" size={17} color={Colors.action} />}
        ></Button>
      </View>
      <View flex>
        <Button
          disabled={
            generatedPricingSuggestions === undefined ||
            generatedPricingSuggestions.length === 0 ||
            selectedIndex === null
          }
          label={t("button.add_generated_product_pricing")}
          backgroundColor={Colors.action}
          color="white"
          onPress={() => {
            if (selectedIndex !== null && generatedPricingSuggestions) {
              const selectedSuggestion =
                generatedPricingSuggestions[selectedIndex];
              onSubmit(selectedSuggestion.price.toFixed(2));
            }
          }}
        ></Button>
      </View>
    </View>
  );

  return (
    <Page
      title={t("title.generated_product_pricing")}
      secondaryTitle={t("title.generated_product_pricing_secondary")}
      footer={footerMarkup}
    >
      <View>
        <FlatActionList items={items} />
      </View>
    </Page>
  );
};
